import { useEffect, useRef, useState } from 'react';
import type { RoomInfo } from '@xiangqi/protocol';
import { MODE_INFO } from '@xiangqi/protocol';
import { Share } from '@capacitor/share';
import { ApiError, api, ensureSession, getPublicWebUrl } from '../api.ts';

interface RoomScreenProps {
  code: string;
  navigate(path: string): void;
}

/**
 * Invite landing: everyone who opens the link becomes a guest and joins.
 * The host waits here (polling every 2s) until an opponent arrives, then
 * both are routed into the match.
 */
export function RoomScreen({ code, navigate }: RoomScreenProps) {
  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const joining = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      if (joining.current) {
        // Another effect instance is mid-join (StrictMode) — poll again soon
        // instead of killing this loop.
        timer = window.setTimeout(() => void tick(), 500);
        return;
      }
      try {
        await ensureSession();
        joining.current = true;
        const res = await api.joinRoom(code);
        joining.current = false;
        if (cancelled) return;
        setRoom(res.room);
        if (res.room.match_id) {
          navigate(`/m/${res.room.match_id}`);
          return;
        }
        timer = window.setTimeout(() => void tick(), 2_000);
      } catch (e) {
        joining.current = false;
        if (cancelled) return;
        if (e instanceof ApiError && e.code === 'ROOM_FULL') {
          setError('Phòng đã đủ người — nhờ chủ phòng tạo link mới nhé.');
        } else if (e instanceof ApiError && e.status === 404) {
          setError('Phòng không tồn tại. Kiểm tra lại link/mã phòng.');
        } else if (e instanceof ApiError && e.status === 410) {
          setError('Phòng đã hết hạn.');
        } else {
          setError('Không kết nối được máy chủ. Đang thử lại…');
          timer = window.setTimeout(() => void tick(), 2_000);
        }
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [code, navigate]);

  const inviteUrl = getPublicWebUrl(`/r/${code}`);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_500);
    } catch {
      // clipboard may be unavailable — the link is selectable below
    }
  };

  const handleShare = async (): Promise<void> => {
    try {
      const canShare = await Share.canShare().then((res) => res.value).catch(() => false);
      if (canShare || (typeof navigator !== 'undefined' && 'share' in navigator)) {
        await Share.share({
          title: `Thách đấu Cờ Tướng (Phòng ${code})`,
          text: `Vào so tài cờ tướng với mình tại phòng ${code} nhé!`,
          url: inviteUrl,
          dialogTitle: 'Chia sẻ phòng đấu cờ tướng',
        });
        return;
      }
    } catch {
      // ignore
    }
    await copy();
  };

  return (
    <main className="page room">
      <h1>Phòng {code}</h1>
      {error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          <p className="waiting" data-testid="room-waiting">
            {room ? `Chế độ: ${MODE_INFO[room.ruleset].label}` : ''}
            {room?.handicap?.time
              ? ` · Chấp giờ (${room.handicap.giver_side === 'red' ? 'Đỏ' : 'Đen'} ${Math.round(room.handicap.time.giver_ms / 60_000)}p vs ${Math.round(room.handicap.time.receiver_ms / 60_000)}p)`
              : ''}
            {room ? ' · ' : ''}
            Đang chờ đối thủ vào phòng…
          </p>
          <div className="invite card">
              <p>Gửi link này cho đối thủ:</p>
              <code className="invite-url" data-testid="invite-url">
                {inviteUrl}
              </code>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="primary" style={{ flex: '1 1 140px' }} onClick={() => void handleShare()}>
                  📲 Chia sẻ lời mời
                </button>
                <button style={{ flex: '1 1 120px' }} onClick={() => void copy()}>
                  {copied ? 'Đã copy ✓' : 'Copy link'}
                </button>
              </div>
            </div>
        </>
      )}
      <button className="link" onClick={() => navigate('/')}>
        ← Về trang chủ
      </button>
    </main>
  );
}
